import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Dropzone from './Dropzone';
import ModelSection from './ModelSection';
import { useOrder } from '../order/OrderContext';
import { useAuth } from '../account/AuthContext';
import GuestModal from '../order/GuestModal';
import apiService from '../services/ApiService';

const Quote = () => {
    const {
        models,
        updateModels,
        updateModel,
        hasActiveCalculations,
        setLeadTime,
        leadTimeDays,
        sessionId,
        clearSession,
        showGrossPrices,
        setShowGrossPrices
    } = useOrder();

    useEffect(() => {
        document.title = "Auto Wycena Druku 3D - Geometry Hustlers";
    }, []);
    const { isAuthenticated } = useAuth();
    const [showGuestModal, setShowGuestModal] = useState(false);
    const navigate = useNavigate();

    // Stan dla "czystej sesji" - brak modeli
    const hasModels = models.length > 0;

    // Obsługa buttona "← nowa wycena"
    const handleNewQuote = async () => {
        try {

            // Oznacz poprzednią sesję jako wygasłą
            await clearSession('expired');


            // Przewiń do góry strony
            window.scrollTo(0, 0);
        } catch (error) {
            // Nawet przy błędzie, wyczyść lokalnie
            await clearSession();
        }
    };

    // Automatyczne wyliczanie lead time przy zmianach modeli
    useEffect(() => {

        const calculateLeadTime = async () => {

            // Filtruj tylko prawidłowe modele z cenami
            const validModels = models.filter(model =>
            !model.error &&
            !model.hasOversizedDimension &&
            !model.priceError &&
            !model.isCalculating &&
            model.priceNet > 0 &&
            model.printTime !== undefined &&
            model.filamentUsed !== undefined &&
            model.material !== undefined
            );


            if (validModels.length === 0) {
                setLeadTime(null);
                return;
            }

            try {
                // Przygotuj dane dla API
                const items = validModels.map(model => ({
                    printTime: model.printTime,
                    filamentUsed: model.filamentUsed,
                    count: model.quantity,
                    hasSupports: model.hasSupports || false,
                    material: model.material
                }));


                // Wywołaj API
                const response = await apiService.estimateDelivery(items);


                setLeadTime(response.leadTimeDays);


            } catch (error) {
                setLeadTime(null);
            }
        };

        calculateLeadTime();
    }, [models]);

    // Obsługa dodawania nowych modeli po upload
    const handleFilesUploaded = (newModels) => {
        const modelsWithDefaults = newModels.map(model => ({
            ...model,
            id: Date.now() + Math.random(),
                                                           technology: 'FDM',
                                                           material: 'PLA',
                                                           color: 'czarny',
                                                           infill: 20,
                                                           quantity: 1,
                                                           priceNet: 0,
                                                           priceGross: 0,
                                                           vat: 0
        }));

        updateModels([...models, ...modelsWithDefaults]);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'file_uploaded' });
    };

    // Obsługa usuwania modelu
    const handleRemoveModel = (modelId) => {
        updateModels(models.filter(model => model.id !== modelId));
    };

    // Obsługa aktualizacji konfiguracji modelu
    const handleUpdateModel = (modelId, updates) => {
        updateModel(modelId, updates);
    };

    // Funkcja obsługi kliknięcia "Złóż zamówienie"
    const handleOrderSubmit = () => {
        if (hasActiveCalculations) return;

        if (isAuthenticated) {
            // Użytkownik zalogowany - przejdź bezpośrednio
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'proceed_to_summary' });
            navigate('/quote/order');
        } else {
            // Użytkownik niezalogowany - pokaż modal
            setShowGuestModal(true);
        }
    };

    // Funkcja obsługi kontynuacji jako gość
    const handleContinueAsGuest = () => {
        setShowGuestModal(false);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'proceed_to_summary' });
        navigate('/quote/order');
    };

    // Funkcja zamykania modala
    const handleCloseModal = () => {
        setShowGuestModal(false);
    };

    return (
        <div className="app">
        {/* Button "← nowa wycena" - tylko gdy są modele */}
        {hasModels && (
            <div style={{ marginBottom: '20px' }}>
            <button
            onClick={handleNewQuote}
            className="btn btn-secondary"
            style={{
                padding: '8px 15px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
            }}
            >
            ← nowa wycena
            </button>
            </div>
        )}

        {!hasModels && (
            <h1 className="app-title">Kalkulator Druku 3D - Sprawdź cenę swoich części online</h1>
        )}

        {/* Podnagłówek - tylko gdy brak modeli */}
        {!hasModels && (
            <h2 style={{
                textAlign: 'center',
                fontSize: '1.2rem',
                fontWeight: '400',
                color: '#666',
                marginTop: '-15px',
                marginBottom: '30px',
                lineHeight: '1.5'
            }}>
            Szybka wycena druku 3D online. Prześlij plik, zobacz cenę i czas realizacji. Bez ukrytych kosztów i bez rejestracji.
            </h2>
        )}

        {/* Najpierw modele (tylko jeśli są) */}
        {models.map(model => (
            <ModelSection
            key={model.id}
            model={model}
            onRemove={() => handleRemoveModel(model.id)}
            onUpdate={(updates) => handleUpdateModel(model.id, updates)}
            />
        ))}

        {/* Komunikat o adblockerze - widoczny tylko przed uploadem */}
        {!hasModels && (
            <div className="alert alert-info" style={{ marginBottom: '20px', textAlign: 'center' }}>
            Jeżeli doświadczasz problemów z przesyłaniem pliku, wyłącz adblocka lub spróbuj w trybie incognito/innej przeglądarce.
            </div>
        )}

        {/* dropzone */}
        <Dropzone onFilesUploaded={handleFilesUploaded} />

        {/* Informacje o wyceniarce - tylko gdy brak modeli */}
        {!hasModels && (
            <div className="container mt-4 p-3">
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
            Jak sprawdzić cenę części z Kalkulatorem Druku 3D
            </h3>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '20px',
                        marginBottom: '30px'
            }}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📁</div>
            <h4>1. Prześlij plik 3D</h4>
            <p style={{ color: '#666', fontSize: '14px' }}>
            Tylko po to, by sprawdzić cenę i czas realizacji. Nie musisz się rejestrować ani podawać danych osobowych na etapie wyceny.
            </p>
            </div>

            <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚙️</div>
            <h4>2. Zobacz wycenę i czas realizacji</h4>
            <p style={{ color: '#666', fontSize: '14px' }}>
            Nasz kalkulator druku 3D pokaże Ci natychmiast cenę i przewidywany termin realizacji - bez oczekiwania na kontakt czy maila zwrotnego.
            </p>
            </div>

            <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📦</div>
            <h4>3. Zamów, jeśli wszystko pasuje</h4>
            <p style={{ color: '#666', fontSize: '14px' }}>
            Sam wybierasz, co dalej - możesz zamówić gotową część albo po prostu zamknąć kalkulator, nie pozostawiając śladu.
            </p>
            </div>
            </div>
            </div>
            </div>
        )}

        {/* podsumowanie (tylko dla prawidłowych modeli) */}

        {(() => {
            // Filtruj tylko prawidłowe modele do podsumowania
            const validModels = models.filter(model =>
            !model.error &&
            !model.hasOversizedDimension &&
            !model.priceError &&
            !model.isCalculating &&
            model.priceNet > 0 &&
            model.printTime !== undefined &&
            model.filamentUsed !== undefined &&
            model.material !== undefined
            );
            if (validModels.length === 0) return null;
            return (
                <div className="container p-3">
                <h3>Szczegóły Twojej wyceny</h3>

                {/* Główny layout: lista pozycji (2/3) + czas realizacji (1/3) */}
                <div className="order-summary-layout">
                {/* LEWA STRONA - Lista pozycji (2/3) */}
                <div className="order-summary-items">
                {validModels.map(model => (
                    <div
                    key={model.id}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 0',
                        borderBottom: '1px solid #eee'
                    }}
                    >
                    <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '2px' }}>
                    {model.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                    {model.technology} • {model.material} • {model.color} • {model.infill}%
                    </div>
                    </div>


                    <div style={{ textAlign: 'right', minWidth: '120px' }}>
                    <div style={{ fontSize: '14px' }}>
                    {model.quantity} × {(showGrossPrices ? model.priceGross : model.priceNet).toFixed(2)} PLN
                    </div>
                    <div style={{ fontWeight: 'bold' }}>
                    {((showGrossPrices ? model.priceGross : model.priceNet) * model.quantity).toFixed(2)} PLN
                    </div>
                    </div>


                    </div>
                ))}
                {/* Komunikat o VAT pod pozycjami, po prawej stronie */}
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#888', marginTop: '8px' }}>
                {showGrossPrices
                    ? "Ceny zawierają VAT 23%"
                    : "Ceny nie uwzględniają podatku VAT"}
                </div>
                </div>

                {/* PRAWA STRONA - Czas realizacji (1/3) */}
                {leadTimeDays && (
                    <div className="order-summary-leadtime">
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>
                    {leadTimeDays > 40 ? '🚀' : '📦'}
                    </div>

                    {leadTimeDays > 40 ? (
                        // KOMUNIKAT DLA DŁUGICH TERMINÓW
                        <div>
                        <div style={{
                            fontSize: '16px',
                            color: '#333',
                            fontWeight: '600',
                            marginBottom: '10px'
                        }}>
                        To większe zlecenie!
                        </div>
                        <div className="alert-warning" style={{
                            fontSize: '14px',
                            padding: '12px',
                            borderRadius: '6px',
                            lineHeight: '1.5'
                        }}>
                        Czas realizacji może wynieść więcej niż 40 dni roboczych.
                        </div>
                        </div>
                    ) : (
                        // STANDARDOWY KOMUNIKAT Z DNIAMI
                        <>
                        <div style={{
                            fontSize: '15px',
                            color: '#333',
                            lineHeight: '1.6',
                            marginBottom: '6px'
                        }}>
                        Przewidywany czas realizacji: <strong>{leadTimeDays} dni rob.*</strong>
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#888',
                            fontStyle: 'italic'
                        }}>
                        *W przypadku zamówienia przed godziną 12:00
                        </div>
                        </>
                    )}
                    </div>
                )}
                </div>

                {/* Podsumowanie cenowe */}

                {(() => {
                    const VAT_RATE = 0.23;
                    const minimumOrderNet = 25;

                    // Sumuj bezpośrednio wartości z backendu (jedyne źródło prawdy)
                    const subtotalNet = +validModels.reduce((sum, model) => sum + (model.priceNet * model.quantity), 0).toFixed(2);
                    const subtotalVat = +validModels.reduce((sum, model) => sum + (model.vat * model.quantity), 0).toFixed(2);
                    const subtotalGross = +validModels.reduce((sum, model) => sum + (model.priceGross * model.quantity), 0).toFixed(2);

                    const isMinimumApplied = subtotalNet < minimumOrderNet;

                    // Jeśli minimalna kwota stosowana, przelicz
                    const finalNet = isMinimumApplied ? minimumOrderNet : subtotalNet;
                    const finalVat = isMinimumApplied ? Number((minimumOrderNet * VAT_RATE).toFixed(2)) : subtotalVat;
                    const finalGross = isMinimumApplied ? Number((finalNet + finalVat).toFixed(2)) : subtotalGross;

                    return (
                        <div>
                        {/* Alert o minimalnej wartości zamówienia */}
                        {isMinimumApplied && (
                            <div className="alert-warning" style={{
                                fontSize: '14px',
                                padding: '12px',
                                borderRadius: '6px',
                                lineHeight: '1.5',
                                marginTop: '15px',
                                marginBottom: '15px',
                                textAlign: 'center'
                            }}>
                            <strong>Minimalna wartość zamówienia: {showGrossPrices
                                ? `${finalGross.toFixed(2)} PLN brutto`
                                : `${minimumOrderNet.toFixed(2)} PLN netto`}</strong>
                            </div>
                        )}

                        <div className="mt-2" style={{ borderTop: '1px solid #ccc', paddingTop: '8px' }}>
                        {/* Linia 1: Wartość części */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>{showGrossPrices ? 'Wartość części:' : 'Wartość części (netto):'}</span>
                        <span><strong>{showGrossPrices ? finalGross.toFixed(2) : finalNet.toFixed(2)} PLN</strong></span>
                        </div>

                        {/* Linia 2: VAT */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '14px' }}>
                        <span>{showGrossPrices ? 'w tym VAT:' : 'Suma VAT:'}</span>
                        <span>{finalVat.toFixed(2)} PLN</span>
                        </div>

                        {/* Linia 3: Suma (zawsze brutto) */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee' }}>
                        <span><strong>Suma:</strong></span>
                        <span><strong>{finalGross.toFixed(2)} PLN</strong></span>
                        </div>
                        {/* Dopisek pod sumą po prawej stronie */}
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#888', marginTop: '4px' }}>
                        Cena nie uwzględnia transportu
                        </div>
                        </div>

                            <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
                            <input
                            type="checkbox"
                            checked={showGrossPrices}
                            onChange={(e) => setShowGrossPrices(e.target.checked)}
                            />
                            <span className="text-small" style={{ color: '#666' }}>
                            Wyświetlaj ceny brutto
                            </span>
                            </label>
                                </div>

                                </div>
                    );
                })()}

                <button
                className="btn btn-primary mt-3"
                style={{
                    padding: "10px 30px",
                    fontSize: "1.1em",
                    opacity: hasActiveCalculations ? 0.6 : 1,
                    cursor: hasActiveCalculations ? 'not-allowed' : 'pointer'
                }}
                onClick={handleOrderSubmit}
                disabled={hasActiveCalculations}
                title={hasActiveCalculations ? 'Poczekaj na zakończenie wyceny wszystkich modeli' : ''}
                >
                {hasActiveCalculations ? 'Obliczanie...' : 'Do podsumowania'}
                </button>
                </div>
            );
        })()}

        <GuestModal
        isOpen={showGuestModal}
        onClose={handleCloseModal}
        onContinueAsGuest={handleContinueAsGuest}
        />
        </div>
    );
};

export default Quote;
